import { FC, memo, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChatbotFileEvent, ChatbotMessage, ChatbotMessageFilter } from '@/models/chatbot';
import AIBubble from '@/storybook/components/AiChatDrawer/components/AIBubble/AIBubble';
import UserBubble from '@/storybook/components/AiChatDrawer/components/UserBubble/UserBubble';
import Button from '@/storybook/components/Button/Button';
import DownloadIcon from '@/storybook/icons/DownloadIcon';
import downloadFile from '@/storybook/utils/downloadFile';
import ChatMarkdown from '../ChatMarkdown/ChatMarkdown';
import ChatImagePreview from '../ChatImagePreview/ChatImagePreview';
import ReasoningSteps from './ReasoningSteps/ReasoningSteps';
import { buildReasoning, parseMessageContent, type ParsedReasoning } from './parseReasoning';
import styles from './ChatMessage.module.css';
import { BYTES_PER_KILOBYTE, isImageFile, isSafeUrl } from './ChatMessage.utils';
import { CHAT_MESSAGE_TEST_IDS } from './testIds';

const GENERIC_REASONING_STATUS_PATTERN = /^The user (is asking|asked|wants|would like|needs|is interested)/i;
const EMPTY_PROMPT_FILTERS: ReadonlyArray<ChatbotMessageFilter> = [];

interface ChatMessageProps {
  message: ChatbotMessage;
}

interface AiMessageContentProps {
  content: string;
  files?: ReadonlyArray<ChatbotFileEvent>;
  promptFilters?: ReadonlyArray<ChatbotMessageFilter>;
  reasoningSteps?: ReadonlyArray<string>;
  trailingContent?: ReactNode;
}
interface FileAttachmentProps {
  file: ChatbotFileEvent;
}

const FileAttachment: FC<FileAttachmentProps> = ({ file }) => {
  const { t } = useTranslation();

  if (!isSafeUrl(file.url)) {
    return null;
  }

  if (isImageFile(file)) {
    return <ChatImagePreview url={file.url} filename={file.filename} sizeBytes={file.size_bytes} />;
  }

  const handleDownload = () => {
    downloadFile(file.url, file.filename);
  };

  return (
    <Button
      variant="text"
      size="S"
      onClick={handleDownload}
      className={styles.fileLink}
      aria-label={t('components.aiChatDrawer.chatMessage.downloadFileAriaLabel', {
        filename: file.filename,
        size: Math.round(file.size_bytes / BYTES_PER_KILOBYTE),
      })}
      data-testid={CHAT_MESSAGE_TEST_IDS.fileLink}
    >
      <DownloadIcon className={styles.fileIcon} />
      <span className={styles.fileName}>{file.filename}</span>
      <span className={styles.fileSize}>({Math.round(file.size_bytes / BYTES_PER_KILOBYTE)} KB)</span>
    </Button>
  );
};

function shouldKeepOriginalStatusAsStep(reasoning: ParsedReasoning, validQueryStatus: string): boolean {
  return Boolean(
    reasoning.statusText &&
    reasoning.statusText !== validQueryStatus &&
    !GENERIC_REASONING_STATUS_PATTERN.test(reasoning.statusText),
  );
}

function resolveDisplayedReasoning(
  reasoning: ParsedReasoning | null,
  promptFilters: ReadonlyArray<ChatbotMessageFilter>,
  validQueryStatus: string,
): ParsedReasoning | null {
  if (!reasoning) {
    return null;
  }

  if (promptFilters.length === 0) {
    return reasoning;
  }

  const originalStatusSteps = shouldKeepOriginalStatusAsStep(reasoning, validQueryStatus) ? [reasoning.statusText] : [];

  return {
    statusText: validQueryStatus,
    steps: [...originalStatusSteps, ...reasoning.steps],
  };
}

export const AiMessageContent = memo<AiMessageContentProps>(
  ({ content, files, promptFilters, reasoningSteps, trailingContent }) => {
    const { t } = useTranslation();
    const availablePromptFilters = promptFilters ?? EMPTY_PROMPT_FILTERS;
    const validQueryStatus = t('components.aiChatDrawer.reasoning.validQueryStatus');

    const parsedMessage = useMemo(() => {
      if (reasoningSteps && reasoningSteps.length > 0) {
        return {
          reasoning: buildReasoning(reasoningSteps),
          response: content,
        };
      }

      return parseMessageContent(content);
    }, [content, reasoningSteps]);

    const reasoning = useMemo<ParsedReasoning | null>(() => {
      return resolveDisplayedReasoning(parsedMessage.reasoning, availablePromptFilters, validQueryStatus);
    }, [availablePromptFilters, parsedMessage.reasoning, validQueryStatus]);

    return (
      <div className={styles.aiContent}>
        {reasoning ? (
          <ReasoningSteps
            reasoning={reasoning}
            filters={availablePromptFilters}
            hasResponse={Boolean(parsedMessage.response)}
          />
        ) : null}
        {parsedMessage.response ? <ChatMarkdown content={parsedMessage.response} /> : null}
        {trailingContent}
        {files && files.length > 0 ? (
          <div className={styles.fileList} data-testid={CHAT_MESSAGE_TEST_IDS.fileList}>
            {files.map(file => (
              <FileAttachment key={`${file.url}-${file.filename}`} file={file} />
            ))}
          </div>
        ) : null}
      </div>
    );
  },
);

const ChatMessage: FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <div data-testid={CHAT_MESSAGE_TEST_IDS.userMessage}>
        <UserBubble>{message.content}</UserBubble>
      </div>
    );
  }

  return (
    <div data-testid={CHAT_MESSAGE_TEST_IDS.aiMessage}>
      <AIBubble copyValue={message.content} hideActions={Boolean(message.interrupted)}>
        <AiMessageContent
          content={message.content}
          files={message.files}
          promptFilters={message.promptFilters}
          reasoningSteps={message.reasoningSteps}
        />
      </AIBubble>
    </div>
  );
};

export default memo(ChatMessage);
